import { useState, useEffect } from 'react';
import { NewsPost, Example } from '../types/content';
import { loadNewsPosts, loadExamples } from '../lib/contentLoader';

/**
 * Hook for progressive content loading - shows content as it loads
 */
export function useProgressiveNews() {
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadProgressively();
  }, []);

  const loadProgressively = async () => {
    try {
      setLoading(true);
      setError(null);

      // Start loading all posts
      const postsPromise = loadNewsPosts();
      
      // Show first few posts quickly for homepage
      const allPosts = await postsPromise;
      
      // Update posts progressively
      setPosts(allPosts);
      setTotalCount(allPosts.length);
      setLoadedCount(allPosts.length);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  return { 
    posts, 
    loading, 
    error, 
    loadedCount, 
    totalCount,
    reload: loadProgressively 
  };
}

/**
 * Hook for batch loading content with progress updates
 */
export function useBatchContent() {
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [examples, setExamples] = useState<Example[]>([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [examplesLoading, setExamplesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBatched();
  }, []);

  const loadBatched = async () => {
    try {
      setError(null);
      
      // Load news and examples in parallel
      const [newsResult, examplesResult] = await Promise.allSettled([
        loadNewsPosts(),
        loadExamples()
      ]);

      // Handle news results
      if (newsResult.status === 'fulfilled') {
        setPosts(newsResult.value);
      } else {
        console.error('Failed to load news:', newsResult.reason);
      }
      setNewsLoading(false);

      // Handle examples results  
      if (examplesResult.status === 'fulfilled') {
        setExamples(examplesResult.value);
      } else {
        console.error('Failed to load examples:', examplesResult.reason);
      }
      setExamplesLoading(false);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content');
      setNewsLoading(false);
      setExamplesLoading(false);
    }
  };

  return {
    posts,
    examples,
    loading: newsLoading || examplesLoading,
    newsLoading,
    examplesLoading,
    error,
    reload: loadBatched
  };
}

/**
 * Hook for progressive examples loading - similar to useProgressiveNews
 */
export function useProgressiveExamples() {
  const [examples, setExamples] = useState<Example[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadProgressively();
  }, []);

  const loadProgressively = async () => {
    try {
      setLoading(true);
      setError(null);

      // Start loading all examples
      const examplesPromise = loadExamples();
      
      // Show examples
      const allExamples = await examplesPromise;
      
      // Update examples progressively
      setExamples(allExamples);
      setTotalCount(allExamples.length);
      setLoadedCount(allExamples.length);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load examples');
    } finally {
      setLoading(false);
    }
  };

  return { 
    examples, 
    loading, 
    error, 
    loadedCount, 
    totalCount,
    reload: loadProgressively 
  };
}